/**
 * Добавляет знак + для положительных чисел
 */
export const plusOrNone = (value: number) => {
  return value >= 0 ? "+" : "";
};

const SUPPORTED_FRACTION_DIGITS = Array.from({ length: 21 }, (_, fractionDigits) => fractionDigits);

const PLAIN_EN_US_FORMATTERS = new Map(
  SUPPORTED_FRACTION_DIGITS.map((maximumFractionDigits) => [
    maximumFractionDigits,
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits,
      useGrouping: false,
    }),
  ])
);

const PERCENTAGE_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const RU_NUMBER_FORMATTERS = new Map(
  SUPPORTED_FRACTION_DIGITS.map((decimals) => [
    decimals,
    new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
  ])
);

const DEFAULT_PLAIN_EN_US_FORMATTER = PLAIN_EN_US_FORMATTERS.get(2)!;
const LOCALIZED_NUMBER_FORMATTERS = new Map<string, Intl.NumberFormat>();

const getLocalizedNumberFormatter = (
  minimumFractionDigits: number,
  maximumFractionDigits: number
): Intl.NumberFormat => {
  const key = `${minimumFractionDigits}:${maximumFractionDigits}`;
  const formatter = LOCALIZED_NUMBER_FORMATTERS.get(key);

  if (formatter) return formatter;

  const nextFormatter = new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits,
    maximumFractionDigits,
  });
  LOCALIZED_NUMBER_FORMATTERS.set(key, nextFormatter);

  return nextFormatter;
};

/**
 * Создает форматтер для цен с динамической точностью
 */
const createPriceFormatter = (price: number): Intl.NumberFormat => {
  let maximumFractionDigits: number;

  if (Math.abs(price) >= 1000) maximumFractionDigits = 2; // BTC: 45000.12
  else if (Math.abs(price) >= 100) maximumFractionDigits = 3; // ETH: 2500.123
  else if (Math.abs(price) >= 10) maximumFractionDigits = 4; // BNB: 450.1234
  else if (Math.abs(price) >= 1) maximumFractionDigits = 5; // ADA: 1.23456
  else if (Math.abs(price) >= 0.1) maximumFractionDigits = 6; // DOGE: 0.123456
  else if (Math.abs(price) >= 0.01) maximumFractionDigits = 7; // TRX: 0.0123456
  else if (Math.abs(price) >= 0.001) maximumFractionDigits = 8; // SHIB: 0.00123456
  else maximumFractionDigits = 10; // Очень маленькие цены

  return PLAIN_EN_US_FORMATTERS.get(maximumFractionDigits) ?? DEFAULT_PLAIN_EN_US_FORMATTER;
};

/**
 * Создает форматтер для количества с учетом типа инструмента
 */
const createQuantityFormatter = (quantity: number, instrumentType?: string): Intl.NumberFormat => {
  let maximumFractionDigits: number;

  // Для контрактов (SWAP) обычно целые числа или с небольшой точностью
  if (instrumentType === "SWAP") {
    if (Math.abs(quantity) >= 1000) maximumFractionDigits = 0; // 15000 контрактов
    else if (Math.abs(quantity) >= 100) maximumFractionDigits = 1; // 150.5 контрактов
    else if (Math.abs(quantity) >= 10) maximumFractionDigits = 2; // 15.25 контрактов
    else maximumFractionDigits = 3; // 1.525 контрактов
  } else {
    // Для спота (монеты)
    if (Math.abs(quantity) >= 1000) maximumFractionDigits = 2; // 15000.12 BTC
    else if (Math.abs(quantity) >= 100) maximumFractionDigits = 3; // 1500.123 ETH
    else if (Math.abs(quantity) >= 10) maximumFractionDigits = 4; // 150.1234 BNB
    else if (Math.abs(quantity) >= 1) maximumFractionDigits = 5; // 15.12345 ADA
    else if (Math.abs(quantity) >= 0.1) maximumFractionDigits = 6; // 1.512345 LINK
    else if (Math.abs(quantity) >= 0.01) maximumFractionDigits = 7; // 0.1512345 ATOM
    else maximumFractionDigits = 8; // Очень маленькие количества
  }

  return PLAIN_EN_US_FORMATTERS.get(maximumFractionDigits) ?? DEFAULT_PLAIN_EN_US_FORMATTER;
};

/**
 * Создает форматтер для денежных значений (USDT)
 */
const createMoneyFormatter = (maximumFractionDigits: number = 2): Intl.NumberFormat => {
  const formatter = PLAIN_EN_US_FORMATTERS.get(maximumFractionDigits);
  if (!formatter) {
    throw new RangeError("maximumFractionDigits must be between 0 and 20");
  }

  return formatter;
};

/**
 * Форматирует цену с динамическим количеством знаков
 * @param price - цена
 * @returns отформатированная строка
 */
export const formatPrice = (price: number | null | undefined): string => {
  if (price === null || price === undefined || typeof price !== "number" || isNaN(price))
    return "-";
  return createPriceFormatter(price).format(price);
};

/**
 * Форматирует количество с динамическим количеством знаков
 * @param quantity - количество
 * @param instrumentType - тип инструмента (SWAP/SPOT)
 * @returns отформатированная строка
 */
export const formatQuantity = (
  quantity: number | null | undefined,
  instrumentType?: string
): string => {
  if (quantity === null || quantity === undefined || typeof quantity !== "number" || isNaN(quantity))
    return "-";
  return createQuantityFormatter(quantity, instrumentType).format(quantity);
};

/**
 * Форматирует денежные значения (USDT) с динамическим количеством знаков
 * @param value - значение в USDT
 * @param maximumFractionDigits - максимальное количество знаков после запятой
 * @returns отформатированная строка
 */
export const formatMoney = (
  value: number | null | undefined,
  maximumFractionDigits: number = 2
): string => {
  if (value === null || value === undefined || typeof value !== "number" || isNaN(value))
    return "-";
  return createMoneyFormatter(maximumFractionDigits).format(value);
};

/**
 * Форматирует число для русскоязычного интерфейса: с запятой в дробной части
 * и разделителями разрядов.
 */
export const formatLocalizedNumber = (
  value: number,
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 2
): string => getLocalizedNumberFormatter(minimumFractionDigits, maximumFractionDigits).format(value);

/**
 * Форматирует проценты
 * @param percentage - процентное значение
 * @returns отформатированная строка с знаком %
 */
export const formatPercentage = (percentage: number | null | undefined): string => {
  if (percentage === null || percentage === undefined || typeof percentage !== "number" || isNaN(percentage))
    return "-";

  // Intl.NumberFormat для процентов ожидает десятичное значение (0.05 = 5%)
  // У нас уже проценты, поэтому делим на 100
  return PERCENTAGE_FORMATTER.format(percentage / 100);
};

/**
 * Форматирует число с разделителями тысяч
 */
export const formatNumber = (value: number, decimals: number = 2): string => {
  const formatter = RU_NUMBER_FORMATTERS.get(decimals);
  if (!formatter) {
    throw new RangeError("decimals must be between 0 and 20");
  }

  return formatter.format(value);
};

/**
 * Форматирует USDT сумму
 */
export const formatUSDT = (value: number | string | null | undefined): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return "0.00 USDT";
  return `${formatNumber(num)} USDT`;
};
